import * as React from 'react'
import { NavigationClose } from '../../../../assets/components/NavigationClose'
import { theme } from '../../../../styles/theme'
import { pluralize } from '../../../../utilities/pluralize'
import { useShoppingCart } from '../../../shop/hooks/useShoppingCart/useShoppingCart'
import { useSpring, animated } from '@react-spring/web'
import { usePrevious } from '../../../../hooks/usePrevious'
import isNil from 'lodash/isNil'
import { useAutoUpdatingRef } from '../../../../hooks/useAutoUpdatingRef'
import { StateSetter } from '../../../../typescript/utility'
import { useIsMounted } from '../../../../hooks/useIsMounted'
import { accessibleNoninteractiveElementClick } from '../../../../common/a11y/accessibleNoninteractiveElementClick'
import { NextLink } from '../../../../common/functionality/NextLink'
import { shopCartRoute } from '../../../../common/constants/routes'
import { NextImage } from '../../../../common/functionality/NextImage'
import { useProductDefaultImages } from '../../../shop/hooks/useProductDefaultImages'
import clsx from 'clsx'
import { visibleCartLineItemsFilter } from '../../../shop/utils/visibleCartLineItemsFilter'
import { getProductShopLink } from '../../../shop/utils/getProductShopLink'
import { usePathname } from 'next/navigation'

const dividerJsx = <div className="h-[1px] w-full bg-grayscale100" />

type Props = {
  isOpen: boolean
  setIsOpen: StateSetter<boolean>
  isHovering: boolean
}

export function EagerCartDropdown({ isOpen: externalIsOpen, setIsOpen, isHovering }: Props) {
  const { cart } = useShoppingCart()
  const { itemCount } = cart

  const isMounted = useIsMounted()
  // want to always start as false and then wait
  const isOpen = externalIsOpen && isMounted

  const timerRef = React.useRef<NodeJS.Timeout | null>(null)

  const previousItemCount = usePrevious(itemCount)

  const itemAdded = itemCount > previousItemCount

  const items = Object.values(cart.items)
  /* clear any timers if we are hovering */
  React.useEffect(() => {
    if (isHovering && !isNil(timerRef.current)) {
      clearTimeout(timerRef.current)
    }
  }, [isHovering])

  const isHoveringRef = useAutoUpdatingRef(isHovering)

  const isOnCartPage = usePathname() === shopCartRoute

  const isOnCartPageRef = useAutoUpdatingRef(isOnCartPage)

  React.useEffect(() => {
    if (itemAdded && !isOnCartPageRef.current) {
      setIsOpen(true)

      if (!isNil(timerRef.current)) {
        clearTimeout(timerRef.current)
      }
      // do not set another timer if we are hovering
      if (isHoveringRef.current) return
      timerRef.current = setTimeout(() => {
        setIsOpen(false)
      }, 5000)
    }
  }, [itemAdded, isHoveringRef, isOpen, setIsOpen, isOnCartPageRef])

  const springProps = useSpring({
    transform: isOpen ? 'scale(1)' : 'scale(0)',
    transformOrigin: 'top',
    config: { friction: 12, tension: 120, clamp: !isOpen },
  })

  const { productDefaultImages } = useProductDefaultImages()

  return (
    <div
      className="absolute"
      {...accessibleNoninteractiveElementClick((e) => {
        e.preventDefault()
        e.stopPropagation()
      })}
    >
      <animated.div style={springProps}>
        {/* add invisible areas in the mouse path from the cart so moving outside the visible white areas still doesn't prematurely trigger a close because hover off happened. the color + opacity is for development. remove opacity-0 to see the area */}
        <div className="absolute h-[50px] w-[50px] -translate-x-1/3 rotate-45 bg-red-900 opacity-0" />
        {/* add invisible areas in the mouse path from the cart so moving outside the visible white areas still doesn't prematurely trigger a close because hover off happened the color + opacity is for development. remove opacity-0 to see the area */}
        <div className="absolute h-[50px] w-[120px] -translate-x-[100px] translate-y-[15px] rotate-[-25deg] bg-blue-900 opacity-0" />
        <svg width="16" height="9" viewBox="0 0 16 9" fill="none" className="absolute -right-[9px] top-4 z-[1]">
          <path d="M8 0L15.5 9H0.5L8 0Z" className="!fill-grayscale00 hover:!fill-grayscale00" />
        </svg>
        <div className="absolute -right-[48px] top-6 w-[264px]">
          <div className="flex w-full cursor-default flex-col gap-4 rounded-lg bg-grayscale00 p-4 shadow-medium">
            <div className="flex items-center justify-between">
              <div className="flex items-baseline">
                <div className="mr-[6px] text-lg font-semibold tracking-[0.36px] text-grayscale900">Cart</div>
                <div className="text-sm font-medium text-grayscale700">
                  {itemCount} {pluralize('Item', itemCount)}
                </div>
              </div>
              <NavigationClose color={theme.colors.grayscale900} size={20} onClick={() => setIsOpen(false)} />
            </div>
            {dividerJsx}
            {itemCount ? (
              items
                .filter((x) => visibleCartLineItemsFilter(x))
                .map((x) => {
                  const showStrikeThrough =
                    x.additionalPriceData?.current.numericCents !== x.additionalPriceData?.freeMember.numericCents

                  const linkUrl = getProductShopLink({
                    slug: x.slug,
                    product_type: x.product_type,
                  })

                  const Wrapper = (props: React.PropsWithChildren<{ className: string }>) =>
                    linkUrl ? <NextLink href={linkUrl} {...props} /> : <div {...props} />
                  return (
                    <Wrapper
                      key={x.product_id}
                      className="inline-flex h-12 flex-col items-center justify-center gap-3 rounded"
                    >
                      <div className="inline-flex items-center justify-center gap-2 self-stretch">
                        <div className="inline-flex h-12 w-12 flex-col items-center justify-center rounded bg-grayscale000 p-1">
                          <NextImage
                            width={40}
                            height={40}
                            className="mix-blend-multiply"
                            src={x.image?.url || productDefaultImages[x.product_type]}
                            alt={x.image?.alternativeText ?? x.name}
                          />
                        </div>
                        <div className="inline-flex shrink grow basis-0 flex-col items-start justify-start gap-0.5">
                          <div className="line-clamp-2 self-stretch break-words text-sm font-semibold text-grayscale900">
                            {x.name}
                          </div>
                          <div className="self-stretch text-[13px] font-medium text-grayscale600">
                            Qty: {x.quantity}
                          </div>
                        </div>
                        <div className="inline-flex flex-col items-end justify-start gap-0.5">
                          <div className="inline-flex items-center justify-center gap-1">
                            <div className="w-[72px] text-right text-sm font-semibold text-grayscale900">
                              {x.additionalPriceData?.current.display}
                            </div>
                          </div>
                          <div
                            className={clsx(
                              'text-[13px] font-medium text-grayscale700 line-through opacity-80',
                              showStrikeThrough ? 'visible' : 'invisible'
                            )}
                          >
                            {x.additionalPriceData?.freeMember.display}
                          </div>
                        </div>
                      </div>
                    </Wrapper>
                  )
                })
            ) : (
              <div className="flex h-12 items-center justify-center text-[15px] font-medium tracking-[0.3px] text-grayscale700">
                Cart is empty
              </div>
            )}
            {dividerJsx}
            <NextLink
              href={shopCartRoute}
              className="flex appearance-none items-center justify-center rounded-full bg-primary700 p-3 px-4 text-sm font-semibold text-grayscale00"
            >
              Go to Cart
            </NextLink>
          </div>
        </div>
      </animated.div>
    </div>
  )
}
